// One document, exactly as extraction left it: the card, the preview frame
// beside the citable surfaces, the lifted images with their vision output, and
// the retrieval chunks. What the extractor produced *is* the support question.
// The Processing tab is the step log behind it (`document-ai/0022` §3).
import { useState, type ReactNode } from "react";
import { FileText } from "lucide-react";
import { Link, useParams } from "react-router";
import {
  adminDocumentAssetContentPath,
  adminDocumentPreviewPath,
} from "@gb/admin-contracts";
import {
  FAMILY_ROLE_LABELS,
  FILE_STATUS_LABELS,
  MADE_BY_LABELS,
  ORGANISE_GAP_LABELS,
  STATUS_DOUBT_LABELS,
  PLACEMENT_RELATION_LABELS,
  type ProjectDocument,
} from "@gb/contracts";
import { AuthedFrame, AuthedImage } from "@/components/gb/authed-file";
import { EmptyState } from "@/components/gb/empty-state";
import { MarkdownView } from "@/components/gb/markdown-view";
import { PageHeader } from "@/components/gb/page-header";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  useDocument,
  useDocumentAssets,
  useDocumentChunks,
  useDocumentProcessingSteps,
  useDocumentSegments,
} from "./documents-api";
import { UndoChangeDialog, type UndoTarget } from "../projects/undo-change-dialog";
import { ProcessingStepsPanel } from "./processing-steps-panel";

export function DocumentViewerPage() {
  const { tenantId = "", projectId = "", docId = "" } = useParams();
  const document = useDocument(tenantId, projectId, docId);
  const segments = useDocumentSegments(tenantId, projectId, docId);
  const assets = useDocumentAssets(tenantId, projectId, docId);
  const chunks = useDocumentChunks(tenantId, projectId, docId);
  const processing = useDocumentProcessingSteps(tenantId, projectId, docId);

  if (document.isError) return <EmptyState icon={FileText} title="Document not found" />;
  if (!document.data) return null;
  const d = document.data;

  return (
    <div>
      <div className="mb-1 text-[0.75rem]">
        <Link
          to={`/tenants/${tenantId}/projects/${projectId}`}
          className="text-brand hover:underline"
        >
          ← Project
        </Link>
      </div>
      <PageHeader eyebrow={d.docType ?? "Document"} title={d.filename} />
      <div className="mt-1 font-mono text-[0.75rem] text-faint">
        {d.mimeType} · {d.pageCount ?? "?"} pages · processing {d.processingStatus ?? "n/a"}
      </div>
      <DocumentCard tenantId={tenantId} projectId={projectId} document={d} />

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="h-[70vh] overflow-hidden rounded-lg border border-border bg-white">
          {/* `previewType` decides, and images get an <img>, not the frame:
              AuthedFrame only frames PDF and plain text, so handing it an
              image/* type renders its refusal for a file the route serves
              fine. Same dispatch as the dashboard's FilePreview. */}
          {d.previewType?.startsWith("image/") ? (
            <AuthedImage
              path={adminDocumentPreviewPath(tenantId, projectId, d.id)}
              alt={d.filename}
              className="h-full w-full object-contain"
            />
          ) : d.previewType ? (
            <AuthedFrame
              path={adminDocumentPreviewPath(tenantId, projectId, d.id)}
              title={d.filename}
              type={d.previewType}
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center text-[0.85rem] text-muted">
              No preview for this file — the extraction is the whole answer.
            </div>
          )}
        </div>

        <Tabs defaultValue="segments" className="min-w-0">
          <TabsList>
            <TabsTrigger value="segments">
              Segments{segments.data ? ` (${segments.data.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="assets">
              Images{assets.data ? ` (${assets.data.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="chunks">
              Chunks{chunks.data ? ` (${chunks.data.length})` : ""}
            </TabsTrigger>
            <TabsTrigger value="processing">Processing</TabsTrigger>
          </TabsList>

          <TabsContent value="segments" className="mt-3 h-[64vh] space-y-3 overflow-y-auto">
            {(segments.data ?? []).map((s) => (
              <div key={s.id} className="rounded-lg border border-border-inner bg-white p-4">
                <div className="mb-2 font-mono text-[0.7rem] text-faint">
                  #{s.sequence + 1} · {s.kind} · {s.label ?? s.pageNo ?? ""}
                </div>
                <MarkdownView
                  text={s.markdown || s.text || "(empty)"}
                  trust="document"
                  className="text-[0.8rem]"
                />
              </div>
            ))}
            {segments.data?.length === 0 && (
              <p className="text-[0.8rem] text-muted">No citable surfaces were extracted.</p>
            )}
          </TabsContent>

          <TabsContent value="assets" className="mt-3 h-[64vh] space-y-3 overflow-y-auto">
            {(assets.data ?? []).map((a) => (
              <div key={a.id} className="rounded-lg border border-border-inner bg-white p-4">
                <div className="flex gap-4">
                  <AuthedImage
                    path={adminDocumentAssetContentPath(tenantId, projectId, d.id, a.id)}
                    alt={a.caption ?? a.role}
                    className="h-28 w-36 shrink-0 rounded object-contain"
                  />
                  <div className="min-w-0">
                    <div className="font-mono text-[0.7rem] text-faint">
                      {a.role}
                      {a.caption ? ` · ${a.caption}` : ""}
                    </div>
                    <p className="mt-1 text-[0.78rem] leading-relaxed text-ink-prose">
                      {a.description === ""
                        ? "(read: nothing in it)"
                        : (a.description ?? "(no vision description)")}
                    </p>
                    {a.ocrText && (
                      <p className="mt-1 font-mono text-[0.7rem] text-muted">{a.ocrText}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {assets.data?.length === 0 && (
              <p className="text-[0.8rem] text-muted">No images were lifted out.</p>
            )}
          </TabsContent>

          <TabsContent value="chunks" className="mt-3 h-[64vh] space-y-3 overflow-y-auto">
            {(chunks.data ?? []).map((c) => (
              <div key={c.id} className="rounded-lg border border-border-inner bg-white p-4">
                <div className="mb-1 font-mono text-[0.7rem] text-faint">
                  #{c.sequence + 1} · {c.citation}
                  {c.tokenEstimate ? ` · ~${c.tokenEstimate} tok` : ""}
                </div>
                <p className="text-[0.78rem] leading-relaxed whitespace-pre-wrap text-ink-prose">
                  {c.text}
                </p>
              </div>
            ))}
            {chunks.data?.length === 0 && (
              <p className="text-[0.8rem] text-muted">No retrieval chunks.</p>
            )}
          </TabsContent>

          <TabsContent value="processing" className="mt-3 h-[64vh] space-y-3 overflow-y-auto">
            <ProcessingStepsPanel steps={processing.data} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/**
 * The card as a user sees it (`document-ai/0015` layer 2), the category it
 * lives in (`document-ai/0032`), what the project check made of it, and where
 * it sits among its versions (`document-ai/0016`). Every field is null until
 * the card is written, the category is "Not sorted yet" until the card or a
 * person sorts the file, and the placement is null until placement has looked
 * at the file. The placement can be undone here when there is a change to
 * reverse, behind the same confirmation as the Categories tab's change log.
 */
function DocumentCard({
  tenantId,
  projectId,
  document: d,
}: {
  tenantId: string;
  projectId: string;
  document: ProjectDocument;
}) {
  const family = d.family;
  const status = d.status;
  const { undoChangeId } = family;
  const [undoing, setUndoing] = useState<UndoTarget | null>(null);
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-4">
      <Fact label="Title">{d.title}</Fact>
      <Fact label="Type">{d.docType}</Fact>
      <Fact label="Document date">{d.documentDate}</Fact>
      <Fact label="Revision">{d.revision}</Fact>
      {/* The tenant's category the file lives in, and who put it there: the
          card's model, or a person who moved it (`document-ai/0032`). Names
          are the tenant's own, from the payload. No category is "Not sorted
          yet", the shelf it is on, rather than a dash. */}
      <Fact label="Category" className="md:col-span-2">
        {d.category ? (
          <>
            {d.category.name}
            <span className="ml-2 font-mono text-[0.68rem] text-faint">
              {MADE_BY_LABELS[d.category.madeBy]}
            </span>
          </>
        ) : (
          "Not sorted yet"
        )}
      </Fact>
      {/* The other categories the card lists, never the main one. Before a
          card is written nobody has looked, so it is a dash, not "None". */}
      <Fact label="Also relevant" className="md:col-span-2">
        {d.alsoRelevant.length > 0
          ? d.alsoRelevant.map((c) => c.name).join(", ")
          : d.card.written
            ? "None"
            : null}
      </Fact>

      {/* What the file is, kept apart from its role among its versions
          (`document-ai/0016` §2). A null status is no claim: either the check
          has not run, or it ran and the file names no project. "Checked at"
          is what tells those two apart, so it says so in words rather than
          leaving a dash. */}
      <Fact label="File status">
        {status.status ? FILE_STATUS_LABELS[status.status] : null}
      </Fact>
      <Fact label="Status reason" className="md:col-span-3">
        {status.reason}
      </Fact>
      <Fact label="Status made by">{status.madeBy ? MADE_BY_LABELS[status.madeBy] : null}</Fact>
      <Fact label="Checked at">
        {status.checkedAt ? new Date(status.checkedAt).toLocaleString() : "Not checked yet"}
      </Fact>
      {/* The backoffice sees every file, hidden ones included
          (`data-model/0013` §8), so this screen has to say out loud what the
          agent's own views do silently. */}
      <Fact label="Hidden from answers" className="md:col-span-2">
        {status.hidden ? (
          <span className="font-semibold text-warn">Yes — the agent cannot read this file.</span>
        ) : (
          "No — the agent may read this file."
        )}
      </Fact>
      {/* A person hid it after the check asked (2026-09-26, HID-12): Remove
          on the dashboard asks twice and names this. */}
      {status.wasAsked && (
        <Fact label="Hidden after a question" className="md:col-span-2">
          {status.wasAsked.doubts.map((doubt) => STATUS_DOUBT_LABELS[doubt.kind]).join(" · ") ||
            "The check was not sure."}
        </Fact>
      )}

      {/* What of it nothing read, and what no step organised (2026-09-26,
          RET-12): the same states the dashboard shows. */}
      <Fact label="Could not be read" className="md:col-span-2">
        {d.readFailure ?? "Nothing. Every step read it, or a try is still to come."}
      </Fact>
      <Fact label="Pictures not read">
        {d.picturesNotRead === null ? "Not counted yet" : String(d.picturesNotRead)}
      </Fact>
      <Fact label="Not organised yet">
        {d.organiseGaps.length > 0 ? d.organiseGaps.map((gap) => ORGANISE_GAP_LABELS[gap]).join(", ") : "None"}
      </Fact>

      <Fact label="Role">{FAMILY_ROLE_LABELS[family.role]}</Fact>
      {/* The file this one defers to: the newer version, the format it
          restates, the signed copy, or the original. */}
      <Fact label="Related file">{family.related?.name}</Fact>
      <Fact label="Family">
        {family.id === null
          ? "On its own"
          : `${family.name ?? "Unnamed"}${family.series ? " · a series" : ""}`}
      </Fact>
      <Fact label="Placed by">
        {family.madeBy ? MADE_BY_LABELS[family.madeBy] : "Not placed yet"}
      </Fact>
      <Fact label="Placement reason" className="md:col-span-3">
        {family.reason || undoChangeId ? (
          <div className="flex items-center justify-between gap-3">
            <span>{family.reason ?? "—"}</span>
            {undoChangeId && (
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0"
                onClick={() =>
                  setUndoing({
                    changeId: undoChangeId,
                    summary: `The placement of “${d.title ?? d.filename}”`,
                  })
                }
              >
                Undo
              </Button>
            )}
          </div>
        ) : null}
      </Fact>
      <Fact label="Placed at">
        {family.placedAt ? new Date(family.placedAt).toLocaleString() : null}
      </Fact>
      {family.members.length > 0 && (
        <Fact label="Other files in the family" className="md:col-span-4">
          <ul>
            {family.members.map((m) => (
              <li key={m.id}>
                {m.name}
                <span className="ml-2 font-mono text-[0.68rem] text-faint">
                  {[FAMILY_ROLE_LABELS[m.role], m.documentDate, m.revision]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        </Fact>
      )}
      {/* The model was not sure, so the file stands on its own until a
          person answers (`document-ai/0016` §4). Read as "This file is … X". */}
      {/* One question per proposed family since 2026-09-26 (VER-08): each file
          asked in it, the first one this file, and what the files show. */}
      {d.question && (
        <Fact label="Question waiting for a person" className="md:col-span-4">
          {d.question.members.map((member) => (
            <div key={member.document.id} className="mt-0.5">
              <div>
                {member.document.id === d.id ? "This file" : `“${member.document.name}”`}:{" "}
                {PLACEMENT_RELATION_LABELS[member.relation]} “{member.other.name}”
              </div>
              <div className="mt-0.5 text-[0.75rem] text-ink-prose">{member.reason}</div>
              {member.why.length > 0 && (
                <div className="mt-0.5 text-[0.72rem] text-muted">{member.why.join(" ")}</div>
              )}
            </div>
          ))}
          {d.question.files.map((file) => (
            <div key={file.document.id} className="mt-1 text-[0.72rem] text-muted">
              {file.document.name}: {file.facts.join(" · ")}
            </div>
          ))}
        </Fact>
      )}
      <UndoChangeDialog
        tenantId={tenantId}
        projectId={projectId}
        target={undoing}
        onClose={() => setUndoing(null)}
      />
    </div>
  );
}

function Fact({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("rounded-md border border-border-inner bg-white px-3 py-2", className)}>
      <div className="text-[0.68rem] font-semibold tracking-wide text-muted uppercase">{label}</div>
      <div className="mt-0.5 text-[0.82rem] text-ink">{children ?? "—"}</div>
    </div>
  );
}
